import React, { useEffect, useRef, useState, useCallback } from "react";
import "./SpeedIndicator.css";
import VSpeedsLegend from "./VSpeedsLegend";
import {
  V_SPEEDS,
  ARC_CONFIG,
  GAUGE_CONFIG,
  SPEED_COLOR_CONFIG,
} from "./gaugeConstants";

const SpeedIndicator = () => {
  const canvasRef = useRef(null);
  const [currentSpeed, setCurrentSpeed] = useState(0);
  const [gpsStatus, setGpsStatus] = useState("initializing");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showPermissionHelp, setShowPermissionHelp] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [testSpeed, setTestSpeed] = useState(60);

  // Custom tooltip state
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    x: 0,
    y: 0,
  });

  // derive color for the current speed from SPEED_COLOR_CONFIG
  const speedColor = (() => {
    const s = Math.max(0, currentSpeed);
    const configs = Object.values(SPEED_COLOR_CONFIG); // declaration order: white, green, yellow, red
    for (let cfg of configs) {
      if (s >= cfg.start && s < cfg.end) return cfg.color;
      // if speed is bigger than the end of the last range, return the red color
      if (s > cfg.end && cfg === configs[configs.length - 1]) {
        return cfg.color;
      }
    }
    return "#ffffff";
  })();

  // Tooltip handlers
  const handleTooltipClick = (event, description) => {
    event.preventDefault();
    event.stopPropagation();

    setTooltip({
      visible: true,
      text: description,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const handleTooltipMouseLeave = () => {
    setTooltip({
      visible: false,
      text: "",
      x: 0,
      y: 0,
    });
  };

  const handleTooltipMouseMove = (event) => {
    if (tooltip.visible) {
      setTooltip((prev) => ({
        ...prev,
        x: event.clientX,
        y: event.clientY,
      }));
    }
  };

  // Apply the computed color to the numeric speed display directly
  useEffect(() => {
    const el = document.querySelector(".speed-value");
    if (el) el.style.color = speedColor;
  }, [speedColor]);

  // Helper functions
  const degToRad = useCallback((degrees) => degrees * (Math.PI / 180), []);

  const speedToAngle = useCallback((speed) => {
    const { minSpeed, maxSpeed } = GAUGE_CONFIG;
    // Full circle starting from 270° (top) going clockwise
    // Map speed (clamped to GAUGE_CONFIG.minSpeed..maxSpeed) linearly to an angle: start at 270° and sweep clockwise across 320°
    const normalizedSpeed = Math.min(Math.max(speed, minSpeed), maxSpeed);
    const angleRange = 320; // 320 degrees
    const startAngle = 270; // Start at 270°
    return (
      startAngle +
      ((normalizedSpeed - minSpeed) / (maxSpeed - minSpeed)) * angleRange
    );
  }, []);

  const drawGaugeBackground = useCallback((ctx) => {
    const { centerX, centerY, radius } = GAUGE_CONFIG;

    // Clear canvas
    ctx.clearRect(0, 0, 320, 320);

    // Draw outer metallic bezel
    const gradient = ctx.createRadialGradient(
      centerX,
      centerY,
      radius - 10,
      centerX,
      centerY,
      radius + 8
    );
    gradient.addColorStop(0, "#555555");
    gradient.addColorStop(0.7, "#333333");
    gradient.addColorStop(1, "#222222");

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius + 8, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();

    // Draw main gauge face - dark background
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fillStyle = "#0f0f0f";
    ctx.fill();

    // Draw inner shadow ring
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius - 3, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#333333";
    ctx.stroke();

    // Draw title "AIRSPEED" at top center
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 14px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("AIRSPEED", centerX, centerY - 60);

    // Draw "KNOTS" at bottom center
    ctx.font = "12px Arial";
    ctx.fillText("KNOTS", centerX, centerY + 35);
  }, []);

  const drawColoredArcs = useCallback(
    (ctx) => {
      const { centerX, centerY, radius } = GAUGE_CONFIG;
      const arcWidth = 10;
      const arcRadius = radius - 30;

      // Flap operating range arc radius
      const whiteArcWidth = 5;
      const whiteArcRadius = arcRadius + 3.5; // Adjusted for visibility

      // Green arc (56-121 knots) - Normal operating range
      ctx.beginPath();
      const greenStartAngle = degToRad(speedToAngle(ARC_CONFIG.green.start));
      const greenEndAngle = degToRad(speedToAngle(ARC_CONFIG.green.end));
      ctx.arc(centerX, centerY, arcRadius, greenStartAngle, greenEndAngle);
      ctx.lineWidth = arcWidth;
      const prevLineCap = ctx.lineCap;
      ctx.lineCap = "butt"; // ensure arc ends are not rounded
      ctx.strokeStyle = ARC_CONFIG.green.color;
      ctx.stroke();
      ctx.lineCap = prevLineCap;

      // Yellow arc (121-148 knots) - Caution range
      ctx.beginPath();
      const yellowStartAngle = degToRad(speedToAngle(ARC_CONFIG.yellow.start));
      const yellowEndAngle = degToRad(speedToAngle(ARC_CONFIG.yellow.end));
      ctx.arc(centerX, centerY, arcRadius, yellowStartAngle, yellowEndAngle);
      ctx.lineWidth = arcWidth;
      ctx.lineCap = "butt"; // ensure arc ends are not rounded
      ctx.strokeStyle = ARC_CONFIG.yellow.color;
      ctx.stroke();
      ctx.lineCap = prevLineCap;

      // Red arc (148-150 knots) - Danger zone
      ctx.beginPath();
      const redStartAngle = degToRad(speedToAngle(ARC_CONFIG.red.start));
      const redEndAngle = degToRad(speedToAngle(ARC_CONFIG.red.end));
      ctx.arc(centerX, centerY, arcRadius, redStartAngle, redEndAngle);
      ctx.lineWidth = arcWidth;
      ctx.lineCap = "butt"; // ensure arc ends are not rounded
      ctx.strokeStyle = ARC_CONFIG.red.color;
      ctx.stroke();
      ctx.lineCap = prevLineCap;

      // White arc (48-100 knots) - Flap operating range
      ctx.beginPath();
      const whiteStartAngle = degToRad(speedToAngle(ARC_CONFIG.white.start));
      const whiteEndAngle = degToRad(speedToAngle(ARC_CONFIG.white.end));
      ctx.arc(centerX, centerY, whiteArcRadius, whiteStartAngle, whiteEndAngle);
      ctx.lineWidth = whiteArcWidth;
      ctx.lineCap = "butt";
      ctx.strokeStyle = ARC_CONFIG.white.color;
      ctx.stroke();
      ctx.lineCap = prevLineCap;
    },
    [degToRad, speedToAngle]
  );

  const drawTickMarks = useCallback(
    (ctx) => {
      const { centerX, centerY, radius } = GAUGE_CONFIG;

      // Major tick marks every 10 knots (0, 10, 20, ..., 150)
      // Only every 20 knots shown the numbers
      const majorTicks = [];
      for (let speed = 40; speed <= 150; speed += 10) {
        majorTicks.push(speed);
      }

      // Minor tick marks every 10 knots (but not on major ticks)
      const minorTicks = [];
      for (let speed = 40; speed <= 150; speed += 5) {
        minorTicks.push(speed);
        if (speed + 10 <= 190) minorTicks.push(speed + 10);
      }

      // Draw major ticks and numbers
      majorTicks.forEach((speed) => {
        const angle = degToRad(speedToAngle(speed));
        const outerRadius = radius - 25;
        const innerRadius = radius - 45;
        const numberRadius = radius - 62;

        // Tick mark
        ctx.beginPath();
        ctx.moveTo(
          centerX + Math.cos(angle) * outerRadius,
          centerY + Math.sin(angle) * outerRadius
        );
        ctx.lineTo(
          centerX + Math.cos(angle) * innerRadius,
          centerY + Math.sin(angle) * innerRadius
        );
        ctx.lineWidth = 3;
        ctx.strokeStyle = "#ffffff";
        ctx.stroke();

        if (speed % 20 !== 0) return; // Skip minor ticks for numbers

        // Numbers
        const numberX = centerX + Math.cos(angle) * numberRadius;
        const numberY = centerY + Math.sin(angle) * numberRadius;

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 16px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(speed.toString(), numberX, numberY);
      });

      // Draw minor ticks
      minorTicks.forEach((speed) => {
        const angle = degToRad(speedToAngle(speed));
        const outerRadius = radius - 25;
        const innerRadius = radius - 34;

        ctx.beginPath();
        ctx.moveTo(
          centerX + Math.cos(angle) * outerRadius,
          centerY + Math.sin(angle) * outerRadius
        );
        ctx.lineTo(
          centerX + Math.cos(angle) * innerRadius,
          centerY + Math.sin(angle) * innerRadius
        );
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#cccccc";
        ctx.stroke();
      });
    },
    [degToRad, speedToAngle]
  );

  const drawVSpeeds = useCallback(
    (ctx) => {
      const { centerX, centerY, radius } = GAUGE_CONFIG;

      V_SPEEDS.forEach(({ speed, label, color }) => {
        if (speed > 200) return; // Skip speeds outside our range

        const angle = degToRad(speedToAngle(speed));

        // Draw V-speed triangle marker
        const markerRadius = radius - 40;
        const markerX = centerX + Math.cos(angle) * markerRadius;
        const markerY = centerY + Math.sin(angle) * markerRadius;

        // Calculate triangle points for better visibility
        const triangleSize = 10;
        const angleOffset = Math.PI / 6; // 30 degrees

        ctx.beginPath();
        ctx.moveTo(markerX, markerY);
        ctx.lineTo(
          markerX - Math.cos(angle - angleOffset) * triangleSize,
          markerY - Math.sin(angle - angleOffset) * triangleSize
        );
        ctx.lineTo(
          markerX - Math.cos(angle + angleOffset) * triangleSize,
          markerY - Math.sin(angle + angleOffset) * triangleSize
        );
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw V-speed label
        const labelRadius = radius - 10;
        const labelX = centerX + Math.cos(angle) * labelRadius;
        const labelY = centerY + Math.sin(angle) * labelRadius;

        ctx.fillStyle = color;
        ctx.font = "bold 12px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, labelX, labelY);
      });
    },
    [degToRad, speedToAngle]
  );

  const drawNeedle = useCallback(
    (ctx, speed) => {
      const { centerX, centerY, radius } = GAUGE_CONFIG;

      // Clamp speed to gauge range
      const clampedSpeed = Math.min(Math.max(speed, 0), 200);
      const needleAngle = degToRad(speedToAngle(clampedSpeed));

      const needleLength = radius - 55;
      const tailLength = 25;

      // Draw needle shadow
      ctx.beginPath();
      ctx.moveTo(centerX + 2, centerY + 2);
      ctx.lineTo(
        centerX + Math.cos(needleAngle) * needleLength + 2,
        centerY + Math.sin(needleAngle) * needleLength + 2
      );
      ctx.lineWidth = 6;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.4)";
      ctx.lineCap = "round";
      ctx.stroke();

      // Draw main needle body (wider at base, tapered)
      const baseWidth = 8;
      const tipWidth = 2;

      ctx.beginPath();
      ctx.moveTo(centerX, centerY);

      // Create tapered needle shape
      const perpAngle1 = needleAngle - Math.PI / 2;
      const perpAngle2 = needleAngle + Math.PI / 2;

      // Base of needle
      ctx.lineTo(
        centerX + (Math.cos(perpAngle1) * baseWidth) / 2,
        centerY + (Math.sin(perpAngle1) * baseWidth) / 2
      );

      // Tip of needle
      const tipX = centerX + Math.cos(needleAngle) * needleLength;
      const tipY = centerY + Math.sin(needleAngle) * needleLength;

      ctx.lineTo(
        tipX + (Math.cos(perpAngle1) * tipWidth) / 2,
        tipY + (Math.sin(perpAngle1) * tipWidth) / 2
      );
      ctx.lineTo(
        tipX + (Math.cos(perpAngle2) * tipWidth) / 2,
        tipY + (Math.sin(perpAngle2) * tipWidth) / 2
      );

      // Other side of base
      ctx.lineTo(
        centerX + (Math.cos(perpAngle2) * baseWidth) / 2,
        centerY + (Math.sin(perpAngle2) * baseWidth) / 2
      );

      ctx.closePath();
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw needle tail
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.lineTo(
        centerX - Math.cos(needleAngle) * tailLength,
        centerY - Math.sin(needleAngle) * tailLength
      );
      ctx.lineWidth = 4;
      ctx.strokeStyle = "#ffffff";
      ctx.lineCap = "round";
      ctx.stroke();

      // Draw center hub
      ctx.beginPath();
      ctx.arc(centerX, centerY, 10, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#000000";
      ctx.stroke();

      // Draw center dot
      ctx.beginPath();
      ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#000000";
      ctx.fill();
    },
    [degToRad, speedToAngle]
  );

  const redrawGauge = useCallback(
    (speed) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;

      drawGaugeBackground(ctx);
      drawColoredArcs(ctx);
      drawTickMarks(ctx);
      drawVSpeeds(ctx);
      drawNeedle(ctx, speed);
    },
    [
      drawGaugeBackground,
      drawColoredArcs,
      drawTickMarks,
      drawVSpeeds,
      drawNeedle,
    ]
  );

  // GPS tracking with improved iOS support
  useEffect(() => {
    let watchId = null;

    const requestLocationPermission = async () => {
      if (!navigator.geolocation) {
        setGpsStatus("unsupported");
        setShowPermissionHelp(true);
        redrawGauge(0);
        return;
      }

      // Check if we're on iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        setGpsStatus("requesting");
        // For iOS, we need to be more explicit about permission request
        navigator.geolocation.getCurrentPosition(
          (position) => {
            // Initial position successful, now start watching
            startWatching();
          },
          (error) => {
            console.error("Initial GPS request failed:", error);
            handleLocationError(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 5000,
          }
        );
      } else {
        startWatching();
      }
    };

    const startWatching = () => {
      setGpsStatus("requesting");

      watchId = navigator.geolocation.watchPosition(
        (position) => {
          const speedMps = position.coords.speed || 0;
          const speedKnots = Math.max(0, speedMps * 1.94384); // Ensure non-negative

          setCurrentSpeed(speedKnots);
          setGpsStatus("active");
          setLastUpdate(new Date());
          setShowPermissionHelp(false);
          redrawGauge(speedKnots);
        },
        (error) => {
          console.error("GPS watch error:", error);
          handleLocationError(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 2000,
        }
      );
    };

    const handleLocationError = (error) => {
      console.log(
        "Location error code:",
        error.code,
        "Message:",
        error.message
      );

      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGpsStatus("permission_denied");
          setShowPermissionHelp(true);
          break;
        case error.POSITION_UNAVAILABLE:
          setGpsStatus("unavailable");
          setShowPermissionHelp(true);
          break;
        case error.TIMEOUT:
          setGpsStatus("timeout");
          // Try again for timeout
          setTimeout(requestLocationPermission, 3000);
          break;
        default:
          setGpsStatus("error");
          setShowPermissionHelp(true);
          break;
      }
      redrawGauge(0);
    };

    requestLocationPermission();

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [redrawGauge]);

  // Test mode for development/demo
  useEffect(() => {
    if (isTestMode) {
      setCurrentSpeed(testSpeed);
      setGpsStatus("test_mode");
      setLastUpdate(new Date());
      redrawGauge(testSpeed);
    }
  }, [isTestMode, testSpeed, redrawGauge]);

  // Initial draw
  useEffect(() => {
    redrawGauge(0);
  }, [redrawGauge]);

  const getStatusColor = () => {
    switch (gpsStatus) {
      case "active":
        return "#00aa00";
      case "requesting":
        return "#ffaa00";
      case "permission_denied":
        return "#ff0000";
      case "unavailable":
        return "#ff6600";
      case "timeout":
        return "#ff9900";
      case "test_mode":
        return "#0099ff";
      case "error":
        return "#ff0000";
      case "unsupported":
        return "#ff0000";
      default:
        return "#888888";
    }
  };

  const getStatusText = () => {
    switch (gpsStatus) {
      case "active":
        return "GPS Active";
      case "requesting":
        return "Requesting GPS...";
      case "permission_denied":
        return "GPS Permission Denied";
      case "unavailable":
        return "GPS Unavailable";
      case "timeout":
        return "GPS Timeout - Retrying...";
      case "test_mode":
        return "Test Mode";
      case "error":
        return "GPS Error";
      case "unsupported":
        return "GPS Unsupported";
      default:
        return "Initializing...";
    }
  };

  const enableTestMode = () => {
    setIsTestMode(true);
    setShowPermissionHelp(false);
  };

  const disableTestMode = () => {
    setIsTestMode(false);
    setCurrentSpeed(0);
    setGpsStatus("initializing");
    // Restart GPS
    window.location.reload();
  };

  return (
    <div className="speed-indicator-container">
      <div className="gauge-wrapper">
        <canvas
          ref={canvasRef}
          width="320"
          height="320"
          className="speed-gauge"
        />
      </div>

      <div className="speed-display">
        <div
          className="speed-value"
          onClick={(e) => {
            // Triple-click "hidden gum" detector using DOM dataset (no extra hooks required)
            const el = e.currentTarget;
            const now = Date.now();
            const last = parseInt(el.dataset.__gumLast || "0", 10);
            let count = parseInt(el.dataset.__gumCount || "0", 10);

            // Reset if too much time passed between clicks
            const THRESHOLD_MS = 800;
            if (now - last > THRESHOLD_MS) count = 0;

            count += 1;
            el.dataset.__gumCount = String(count);
            el.dataset.__gumLast = String(now);

            if (count >= 3) {
              // trigger test mode and clear counters
              enableTestMode();
              el.dataset.__gumCount = "0";
              el.dataset.__gumLast = "0";
            }
          }}
        >
          {Math.round(currentSpeed)}
        </div>
        <div className="speed-unit">KNOTS</div>
      </div>

      <div className="status-panel">
        <div className="status-indicator">
          <span
            className="status-dot"
            style={{ backgroundColor: getStatusColor() }}
          ></span>
          <span className="status-text">{getStatusText()}</span>
        </div>

        {showPermissionHelp && (
          <div className="permission-help">
            <h4>📍 GPS Permission Needed</h4>
            <div className="help-content">
              <p>
                <strong>For iPhone/iPad users:</strong>
              </p>
              <ol>
                <li>Open Safari Settings → Privacy & Security</li>
                <li>Tap "Location Services"</li>
                <li>Enable location for Safari</li>
                <li>Refresh this page</li>
                <li>Tap "Allow" when prompted</li>
              </ol>

              <p>
                <strong>Alternative:</strong>
              </p>
              <button className="test-mode-btn" onClick={enableTestMode}>
                🧪 Enable Test Mode
              </button>
              <p className="test-note">
                Test mode lets you simulate different speeds
              </p>
            </div>
          </div>
        )}

        {isTestMode && (
          <div className="test-controls">
            <h4>🧪 Test Mode Controls</h4>
            <div className="speed-control">
              <label>Speed: {testSpeed} knots</label>
              <input
                type="range"
                min="0"
                max="200"
                value={testSpeed}
                onChange={(e) => setTestSpeed(Number(e.target.value))}
                className="speed-slider"
              />
              <div className="test-buttons">
                <button onClick={() => setTestSpeed(0)}>0</button>

                {V_SPEEDS.map(({ speed, label, description }) => (
                  <button
                    key={label}
                    onClick={(e) => {
                      setTestSpeed(speed);
                      handleTooltipClick(e, description);
                    }}
                    onMouseLeave={handleTooltipMouseLeave}
                    onMouseMove={handleTooltipMouseMove}
                    aria-label={`${label} ${speed} knots ${description}`}
                  >
                    {label}
                  </button>
                ))}

                <button onClick={() => setTestSpeed(200)}>Max</button>
              </div>
            </div>
            <button className="disable-test-btn" onClick={disableTestMode}>
              📡 Try GPS Again
            </button>
          </div>
        )}

        {lastUpdate && !isTestMode && (
          <div className="last-update">
            Last Update: {lastUpdate.toLocaleTimeString()}
          </div>
        )}

        <VSpeedsLegend
          vSpeeds={V_SPEEDS}
          onItemClick={handleTooltipClick}
          onItemMouseMove={handleTooltipMouseMove}
          onItemMouseLeave={handleTooltipMouseLeave}
        />

        {/* Custom Tooltip */}
        {tooltip.visible && (
          <div
            className="custom-tooltip"
            style={{
              position: "fixed",
              left: tooltip.x + 10,
              top: tooltip.y - 35,
              zIndex: 1000,
              pointerEvents: "none",
            }}
          >
            {tooltip.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeedIndicator;
